/**
 * SKU Group Repository
 * Handles all SKU group-related API calls
 */

import { apiClient } from '../../client'

import type { PaginatedResponse } from '../role.repository'

export interface SkuGroup {
    id: number
    tenant_id: number

    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date

    ref_code?: string
}

export interface CreateSkuGroupRequest {
    tenant_id: number

    en_name: string
    ar_name?: string
    status?: number
    ref_code?: string

}

export interface UpdateSkuGroupRequest {
    tenant_id?: number

    en_name?: string
    ar_name?: string
    status?: number
    ref_code?: string

}

class SkuGroupRepository {
    async getAll(page: number = 1, limit: number = 25, filters?: any): Promise<PaginatedResponse<SkuGroup>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }

            if (filters.sku_group_id) {
                params.append('sku_group_id', filters.sku_group_id)
            }
        }

        return apiClient.get<PaginatedResponse<SkuGroup>>(`/sku-groups?${params.toString()}`)
    }

    async getById(id: number): Promise<SkuGroup> {
        return apiClient.get<SkuGroup>(`/sku-groups/${id}`)
    }

    async create(data: CreateSkuGroupRequest): Promise<SkuGroup> {
        return apiClient.post<SkuGroup>('/sku-groups', data)
    }

    async update(id: number, data: UpdateSkuGroupRequest): Promise<SkuGroup> {
        return apiClient.put<SkuGroup>(`/sku-groups/${id}`, data)
    }

    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/sku-groups/${id}`)
    }

    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }

        if (filters?.sku_group_id) {
            params.append('sku_group_id', filters.sku_group_id.toString())
        }

        const baseUrl = `/sku-groups/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
}

export const skuGroupRepository = new SkuGroupRepository()

